<template>
  <div>
    <div class="bg-blue-900 p-4 lg:p-12">
      <div class="max-w-5xl mx-auto mb-12">
        <div class="flex flex-col md:flex-row md:items-center">
          <h4 class="text-white font-bold">Payments</h4>

          <div class="flex md:ml-auto mt-4 md:mt-0">
            <BaseButton
              tag="router-link"
              :to="{ name: 'admin.payments.create' }"
              primary
              class="ml-auto md:ml-4"
            >
              New Payment
            </BaseButton>
          </div>
        </div>
      </div>
    </div>
    <div class="px-4 pb-4 lg:px-12 lg:pb-12 -mt-12">
      <div class="max-w-5xl mx-auto">
        <div class="space-y-16">
          <VList :meta="meta">
            <template #before>
              <div class="hidden lg:flex font-medium text-sm text-gray-600 mb-4 pr-2">
                <div class="w-32 px-4">Date</div>
                <div class="flex-1 px-4">From</div>
                <div class="flex-1 px-4">To</div>
                <div class="flex-1 px-4">Bank Account</div>
                <div class="w-32 px-4 text-right mr-12">Amount</div>
              </div>
            </template>

            <template v-if="isLoading" #default>
              <div class="odd:bg-gray-100 border border-gray-200 rounded p-4 text-center">
                <Loader />
              </div>
            </template>

            <template v-else-if="isRejected" #default>
              <div
                class="odd:bg-red-100 border border-red-200 rounded p-4 text-red-600 text-center"
              >
                Something went wrong. Please try again later.
              </div>
            </template>

            <template v-else-if="payments.length" #default>
              <template v-for="payment in payments">
                <VListLine
                  :key="`payments_${payment.uuid}`"
                  :payment="payment"
                  :disabled="isLoading"
                  @payment:view="viewPayment"
                />
              </template>
            </template>

            <template v-else #default>
              <div class="odd:bg-gray-100 border border-gray-200 rounded p-4 text-center">
                No results found
              </div>
            </template>
          </VList>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import store from '@/store';
import VList from '@/components/Common/VList.vue';
import VListLine from '@/components/Payments/VListLine.vue';

const STATUS_IDLE = 'idle';
const STATUS_PENDING = 'pending';
const STATUS_RESOLVED = 'resolved';
const STATUS_REJECTED = 'rejected';

export default {
  name: 'Payments',

  components: {
    VList,
    VListLine,
  },

  data: () => ({
    status: STATUS_IDLE,
  }),

  computed: {
    ...mapState('admin/payments', ['meta']),
    ...mapGetters('admin/payments', ['payments']),
    isLoading() {
      return this.status === STATUS_PENDING;
    },
    isResolved() {
      return this.status === STATUS_RESOLVED;
    },
    isRejected() {
      return this.status === STATUS_REJECTED;
    },
  },

  watch: {
    $route: 'fetchData',
  },

  async beforeRouteEnter(to, from, next) {
    const { query: params } = to;

    try {
      await store.dispatch('admin/payments/load', { params });
      next();
    } catch (error) {
      next(false);
    }
  },

  destroyed() {
    this.setIds([]);
    this.setMeta(null);
    this.setLinks(null);
  },

  methods: {
    ...mapActions('admin/payments', {
      loadPayments: 'load',
    }),

    ...mapMutations('admin/payments', ['setIds', 'setMeta', 'setLinks']),

    async fetchData() {
      const { query: params } = this.$route;

      this.status = STATUS_PENDING;

      try {
        await this.loadPayments({ params });

        this.status = STATUS_RESOLVED;
      } catch (error) {
        this.status = STATUS_REJECTED;
      }
    },

    viewPayment(payment) {
      if (!payment.can.view) return;

      this.$router.push({
        name: 'admin.payments.paymentUuid',
        params: {
          paymentUuid: payment.uuid,
        },
      });
    },
  },
};
</script>
